import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useAppConfig } from './AppConfigContext';
import authService from '../services/authService';
import { CompanySettingsService } from '../api/services/CompanySettingsService';
import { CompanyWorkScheduleService } from '../api/services/CompanyWorkScheduleService';
import { CompanyLocationService } from '../api/services/CompanyLocationService';
import { HolidayService } from '../api/services/HolidayService';
import type { Routemize_DTOs_CompanySettingsDTO } from '../api/models/Routemize_DTOs_CompanySettingsDTO';
import type { Routemize_DTOs_EnhancedWorkScheduleDTO } from '../api/models/Routemize_DTOs_EnhancedWorkScheduleDTO';
import type { Routemize_DTOs_CompanyLocationDto } from '../api/models/Routemize_DTOs_CompanyLocationDto';
import type { Routemize_DTOs_Holidays_HolidayDto } from '../api/models/Routemize_DTOs_Holidays_HolidayDto';
import { resolveBrandSettings, type BrandSettings } from '../utils/brandSettings';
import { UserService } from '../api/services/UserService';
import { UserRoleConfigManager } from '../business/users/UserRoleConfig';
import { mapTeamMemberListDto, type TeamMember } from '../business/users/UserManager';
import { subscribeTeamMembersInvalidation } from '../business/users/teamMembersCache';
import { subscribeSetupChanges } from '../business/settings/setupEvents';
import type { Routemize_DTOs_TeamMemberListDto } from '../api/models/Routemize_DTOs_TeamMemberListDto';

export interface RoutePreferences {
  defaultStartingLocation: 'home' | 'office';
  defaultEndingLocation: 'home' | 'office';
  firstAppointmentScheduling: 'starts_at_work_hours' | 'includes_travel_time';
  lastAppointmentScheduling: 'ends_at_work_hours' | 'includes_return_travel';
}

export interface OfficeLocation {
  address: string;
  coordinates: { lat: number; lng: number };
}

interface TenantDataContextType {
  companySettings: Routemize_DTOs_CompanySettingsDTO | null;
  /** Display-ready brand values (logo/colors/name) derived from companySettings, with platform defaults. */
  branding: BrandSettings;
  routePreferences: RoutePreferences | null;
  companyTimezone: string | undefined;
  /** Tenant primary country (ISO alpha-2). Drives labels (State/Province, ZIP/Postal) and units (mi/km). */
  country: 'US' | 'CA';
  officeLocation: OfficeLocation | null;
  companyLocations: Routemize_DTOs_CompanyLocationDto[];
  workSchedule: Routemize_DTOs_EnhancedWorkScheduleDTO | null;
  holidays: Routemize_DTOs_Holidays_HolidayDto[];
  defaultBookingFormLink: string | null;
  /**
   * Cached team-member roster. Rarely changes, yet it was being fetched on
   * nearly every page — so it lives here (loaded once on auth, refetched only
   * when a user mutation invalidates it via UserManager + teamMembersCache).
   * Consume through the `useTeamMembers()` selector below.
   */
  rawTeamMembers: Routemize_DTOs_TeamMemberListDto[];
  teamMembers: TeamMember[];
  assignableTeamMembers: TeamMember[];
  refreshTeamMembers: () => Promise<void>;
  isLoading: boolean;
  refreshHolidays: (year: number) => void;
  /**
   * Re-fetches `companySettings` from the API and updates the context.
   * Settings panels (e.g. SmartAssignmentPanel, RoutePreferencesPanel)
   * should call this after a successful save so consumers reading from
   * the context — like CreateBookingModal — see the new state without
   * a full page reload.
   */
  refreshCompanySettings: () => Promise<void>;
}

const TenantDataContext = createContext<TenantDataContextType | null>(null);

function isAssignableTeamMember(member: Routemize_DTOs_TeamMemberListDto): boolean {
  if (!member.isActive) return false;
  return UserRoleConfigManager.canBeAssignedToEvents(
    UserRoleConfigManager.determineRoleType(member.roleNames || [])
  );
}

function resolveOfficeLocation(locations: Routemize_DTOs_CompanyLocationDto[]): OfficeLocation | null {
  if (!locations.length) return null;

  // Priority 1: default location
  let office = locations.find(l => l.isDefault === true);

  if (!office) {
    // Priority 2: HomeOffice (type 1), then MainOffice (type 2)
    office = locations.find(l => l.locationType === 1 || String(l.locationType).toLowerCase() === 'homeoffice');
    if (!office) {
      office = locations.find(l => l.locationType === 2 || String(l.locationType).toLowerCase() === 'mainoffice');
    }
    // Priority 3: first available
    if (!office) office = locations[0];
  }

  if (!office?.latitude || !office?.longitude) return null;

  const address = [
    office.addressLine1,
    office.addressLine2,
    `${office.city}, ${office.state} ${office.zipCode}`
  ].filter(Boolean).join(', ');

  return { address, coordinates: { lat: office.latitude, lng: office.longitude } };
}

function parseRoutePreferences(settings: Routemize_DTOs_CompanySettingsDTO): RoutePreferences | null {
  const prefs = settings.routePreferences;
  if (!prefs) return null;
  return {
    defaultStartingLocation: (prefs.defaultStartingLocation as 'home' | 'office') || 'home',
    defaultEndingLocation: (prefs.defaultEndingLocation as 'home' | 'office') || 'home',
    firstAppointmentScheduling: (prefs.firstAppointmentScheduling as 'starts_at_work_hours' | 'includes_travel_time') || 'includes_travel_time',
    lastAppointmentScheduling: (prefs.lastAppointmentScheduling as 'ends_at_work_hours' | 'includes_return_travel') || 'includes_return_travel',
  };
}

export function TenantDataProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { config } = useAppConfig();
  // Track which tenant's data is loaded so impersonation (tenant switch) refetches.
  const loadedTenantRef = useRef<string | null>(null);

  const [companySettings, setCompanySettings] = useState<Routemize_DTOs_CompanySettingsDTO | null>(null);
  const [routePreferences, setRoutePreferences] = useState<RoutePreferences | null>(null);
  const [officeLocation, setOfficeLocation] = useState<OfficeLocation | null>(null);
  const [companyLocations, setCompanyLocations] = useState<Routemize_DTOs_CompanyLocationDto[]>([]);
  const [workSchedule, setWorkSchedule] = useState<Routemize_DTOs_EnhancedWorkScheduleDTO | null>(null);
  const [holidays, setHolidays] = useState<Routemize_DTOs_Holidays_HolidayDto[]>([]);
  const [rawTeamMembers, setRawTeamMembers] = useState<Routemize_DTOs_TeamMemberListDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const holidayYearRef = useRef<number | null>(null);

  const refreshHolidays = useCallback((year: number) => {
    if (holidayYearRef.current === year) return;
    holidayYearRef.current = year;
    HolidayService.getApiAppHolidayGetList(year)
      .then(data => setHolidays(data.filter(h => h.isEnabled)))
      .catch(() => setHolidays([]));
  }, []);

  const refreshCompanySettings = useCallback(async () => {
    try {
      const settings = await CompanySettingsService.getApiAppCompanySettingsGet();
      setCompanySettings(settings);
      setRoutePreferences(parseRoutePreferences(settings));
    } catch (error) {
      console.error('Failed to refresh company settings:', error);
    }
  }, []);

  const refreshTeamMembers = useCallback(async () => {
    try {
      const members = await UserService.getApiAppUserGetTeamMembers();
      setRawTeamMembers(members ?? []);
    } catch (error) {
      console.error('Failed to refresh team members:', error);
    }
  }, []);

  const refreshWorkSchedule = useCallback(async () => {
    try {
      setWorkSchedule(await CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule());
    } catch (error) {
      console.error('Failed to refresh work schedule:', error);
    }
  }, []);

  const refreshCompanyLocations = useCallback(async () => {
    try {
      const locs = (await CompanyLocationService.getApiAppCompanyLocationGetList()).items ?? [];
      setCompanyLocations(locs);
      setOfficeLocation(resolveOfficeLocation(locs));
    } catch (error) {
      console.error('Failed to refresh company locations:', error);
    }
  }, []);

  // Refetch the roster whenever a user mutation invalidates it (create / update
  // / delete / role change). Non-React code fires the signal; the returned
  // value is the unsubscribe cleanup.
  useEffect(() => subscribeTeamMembersInvalidation(() => { refreshTeamMembers(); }), [refreshTeamMembers]);

  // Same for setup Routy changes from chat while the pages showing it stay open.
  useEffect(() => subscribeSetupChanges((area) => {
    if (area === 'company_settings') void refreshCompanySettings();
    else if (area === 'company_hours') void refreshWorkSchedule();
    else if (area === 'locations') void refreshCompanyLocations();
    else if (area === 'team') void refreshTeamMembers();
  }), [refreshCompanySettings, refreshWorkSchedule, refreshCompanyLocations, refreshTeamMembers]);

  useEffect(() => {
    // The iframe embed (/embed/new-booking) authenticates with an in-memory embed token and has
    // no OIDC user, so isAuthenticated stays false there. Without this, company settings never
    // load and companyTimezone falls back to UTC (exact-time bookings saved hours early).
    // EmbedTokenRoute sets the token then refreshes config, which re-runs this effect.
    if (authLoading || (!isAuthenticated && !authService.hasEmbedToken())) return;
    const tenantId = config?.currentUser?.tenantId;
    if (!tenantId) { setIsLoading(false); return; }
    // Reload when the tenant changes (e.g. impersonation start/stop), not just once.
    if (loadedTenantRef.current === tenantId) return;
    loadedTenantRef.current = tenantId;
    setIsLoading(true);

    const load = async () => {
      try {
        const [settings, schedule, locationsResp, members] = await Promise.all([
          CompanySettingsService.getApiAppCompanySettingsGet(),
          CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule(),
          CompanyLocationService.getApiAppCompanyLocationGetList(),
          // Resilient: a 403 for roles without roster access must not take down
          // the rest of the tenant data load.
          UserService.getApiAppUserGetTeamMembers().catch(() => [] as Routemize_DTOs_TeamMemberListDto[]),
        ]);

        setCompanySettings(settings);
        setRoutePreferences(parseRoutePreferences(settings));
        setWorkSchedule(schedule);
        setRawTeamMembers(members ?? []);

        const locs = locationsResp.items ?? [];
        setCompanyLocations(locs);
        setOfficeLocation(resolveOfficeLocation(locs));

        // Load holidays for current year
        const year = new Date().getFullYear();
        holidayYearRef.current = year;
        HolidayService.getApiAppHolidayGetList(year)
          .then(data => setHolidays(data.filter(h => h.isEnabled)))
          .catch(() => setHolidays([]));
      } catch (error) {
        console.error('Failed to load company data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isAuthenticated, authLoading, config]);

  const branding = useMemo(() => resolveBrandSettings(companySettings), [companySettings]);

  const teamMembers = useMemo(
    () => rawTeamMembers.filter(m => m.isActive).map(m => mapTeamMemberListDto(m)),
    [rawTeamMembers]
  );
  const assignableTeamMembers = useMemo(
    () => rawTeamMembers.filter(isAssignableTeamMember).map(m => mapTeamMemberListDto(m)),
    [rawTeamMembers]
  );

  // Tenant country (ISO alpha-2).
  const country: 'US' | 'CA' = companySettings?.country === 'CA' ? 'CA' : 'US';

  const value: TenantDataContextType = {
    companySettings,
    branding,
    routePreferences,
    companyTimezone: companySettings?.timeZone ?? undefined,
    country,
    officeLocation,
    companyLocations,
    workSchedule,
    holidays,
    defaultBookingFormLink: companySettings?.defaultRoutemizeLink ?? null,
    rawTeamMembers,
    teamMembers,
    assignableTeamMembers,
    refreshTeamMembers,
    isLoading,
    refreshHolidays,
    refreshCompanySettings,
  };

  return (
    <TenantDataContext.Provider value={value}>
      {children}
    </TenantDataContext.Provider>
  );
}

export function useTenantData(): TenantDataContextType {
  const ctx = useContext(TenantDataContext);
  if (!ctx) throw new Error('useTenantData must be used within TenantDataProvider');
  return ctx;
}

/**
 * Non-throwing variant for components shared with /admin/* pages, which render
 * outside TenantDataProvider. Returns null there.
 */
export function useOptionalTenantData(): TenantDataContextType | null {
  return useContext(TenantDataContext);
}

export interface TeamMembersSlice {
  rawTeamMembers: Routemize_DTOs_TeamMemberListDto[];
  teamMembers: TeamMember[];
  assignableTeamMembers: TeamMember[];
  isLoading: boolean;
  refreshTeamMembers: () => Promise<void>;
}

/**
 * Selector over TenantDataContext for the cached team-member roster. Kept as a
 * dedicated hook so call sites read team data by intent, not by reaching into
 * the whole company-data bag.
 */
export function useTeamMembers(): TeamMembersSlice {
  const { rawTeamMembers, teamMembers, assignableTeamMembers, isLoading, refreshTeamMembers } = useTenantData();
  return { rawTeamMembers, teamMembers, assignableTeamMembers, isLoading, refreshTeamMembers };
}
